import { TableOptions } from '@/components/data-table/DataTable';
import { Brand } from '@/models/Brand';
import { Category } from '@/models/Category';
import { Color } from '@/models/Color';
import { FastProductVariant } from '@/models/FastProductVariant';
import { ProductImage } from '@/models/Product';
import { ProductVariant } from '@/models/ProductVariant';
import { PurchaseOrderItem, PurchaseOrderItemKeyInfo } from '@/models/PurchaseOrderItem';
import { Season } from '@/models/Season';
import { Size } from '@/models/Size';
import { Supplier } from '@/models/Supplier';
import ErrorHandler from '@/support/ErrorHandler';
import { thumbnailTransformer } from '@/support/ThumbnailTransformer';
import debounce from 'debounce';
import { isArray } from 'lodash';
import { Component, Prop, Vue } from 'vue-property-decorator';

@Component<CreatePurchaseOrderItemCard>({})
export default class CreatePurchaseOrderItemCard extends Vue {
  // #region @Props
  @Prop({ required: true})
  protected purchaseOrderId!: string;
  // #endregion

  // #region @Refs
  // #endregion

  // #region Props

  protected PurchaseOrderItemKeyInfo = PurchaseOrderItemKeyInfo;

  protected isCreatingOrder = false;

  public setTableFiltersDebounce: () => void = debounce(this.setTableFilters, 400);

  protected tableFilters: Record<string, string | string[]> = {};

  protected productVariantQuantity: Record< string, number> = {};

  protected productVariantSearchPhrase: string = '';

  protected selectedBrands: Brand[] = [];

  protected brandModel: Brand = new Brand().limit(50);

  // #endregion

  // #region Plugin props
  // #endregion

  // #region Lifecycle hooks / Init
  // #endregion

  // #region Handlers

  protected setTableFilters(): void {
    this.tableFilters = {
      ...this.productVariantSearchPhrase
        ? { search: this.productVariantSearchPhrase }
        : {},
      ...this.selectedBrands.length
        ? { brands: this.selectedBrands.map((brand) => brand.id || '') }
        : {},
    };
  }

  // #endregion

  // #region Helpers

  // #endregion

  // #region API Requests

  protected async createOrderItem(variant: ProductVariant): Promise<void> {
    if (! this.purchaseOrderId) { return; }

    this.$emit('orderItem:creating');
    this.isCreatingOrder = true;

    let quantity = 1;
    if (variant.id && this.productVariantQuantity[variant.id]) {
      quantity = this.productVariantQuantity[variant.id];
    }

    try {
      const purchaseOrderItem = new PurchaseOrderItem();

      const response = await purchaseOrderItem.create({
        purchase_order: this.purchaseOrderId,
        product_variant: variant.id,
        quantity,
      });

      if (response) {
        this.$emit('orderItem:created');
      }
    } catch (error) {
      ErrorHandler.network(error);
    } finally {
      this.isCreatingOrder = false;
    }
  }
  // #endregion

  // #region Getters & Setters

  protected get isSetSearchFilters(): boolean {
    return !! Object.keys(this.tableFilters).length;
  }

  protected get tableOptions(): TableOptions {
    return {
      model: new FastProductVariant()
        .include(['color', 'size', 'brand', 'season', 'supplier', 'categories', 'stock'])
        .filter(this.tableFilters)
        .limit(50),
      headers: [
          {
            text: '',
            value: 'image_url',
            width: '64px',
            transform: (image: ProductImage) => image && image.url_large ? thumbnailTransformer(image.url_large) : '',
          },
          {
            text: `<span title="Product Name / EAN / SKU / Supplier Style name">Product Name</span>`,
            value: 'product_name',
            action: '/varianten/{id}',
            sortable: {
              key: 'product_name',
              order: 'DESC'
            },
            transform: (name: string, variant: ProductVariant) => {
              return `<div class='custom-name'>
                      ${ variant.name ? `<span><strong>${variant.name}</strong></span>` : ''}
                      ${ variant.upc ? `<span>${variant.upc}</span>` : ''}
                      ${ variant.sku ? `<span class='text-grey'>${variant.sku}</span>` : ''}
                      ${ variant.supplier_style_name ? `<span class='text-light-grey'>${variant.supplier_style_name}</span>` : ''}
                      </div>
                      `;
            }
          },
          {
            text: 'Supplier / Brand',
            value: 'brand',
            transform: (value: Brand, productVariant: ProductVariant) => {
              const brand = (productVariant.brand as Brand);
              const supplier = (productVariant.supplier as Supplier);
              return [
                brand && brand.name ? brand.name : null,
                supplier && supplier.name ? supplier.name : null,
              ].filter(x => x).join(' <br> ');
            }
          },
          {
            text: 'Category',
            value: 'category',
            sortable: {
              key: 'category',
              order: 'DESC'
            },
            transform: (name: string, product: ProductVariant) => {
              if (product.hasOwnProperty('categories')) {
                return (product.categories as Category[])
                  .map(categoryObj => categoryObj.name)
                  .sort()
                  .join(', ');
              } else {
                return '';
              }
            }
          },
          {
            text: 'Color / Size',
            value: 'brand',
            transform: (value: Brand, productVariant: ProductVariant) => {
              const color = (productVariant.color as Color);
              const size = (productVariant.size as Size);
              return [
                color && color.name ? color.name : null,
                size && size.name ? size.name : null,
              ].filter(x => x).join(' <br> ');
            }
          },
          {
            text: 'Supplier Color / Size',
            value: 'brand',
            transform: (value: Brand, productVariant: ProductVariant) => {
              return [
                productVariant && productVariant.supplier_color ? productVariant.supplier_color : null,
                productVariant && productVariant.supplier_size ? productVariant.supplier_size : null,
              ].filter(x => x).join(' <br> ');
            }
          },
          {
            text: 'Season',
            value: 'season',
            transform: (season: Season) => {
              return season && season.name ? season.name : '';
            }
          },
          {
            text: 'Price',
            value: 'created_at',
            align: 'right',
            class: 'text-right',
            transform: (date: string, productVariant: ProductVariant) => {
              return `<div class="custom-name">
                      ${ productVariant.retail_price ? `<span class="d-block white-space-no-wrap">R: € ${productVariant.retail_price}</span>` : ''}
                      ${ productVariant.buy_price ? `<span class="d-block white-space-no-wrap">B: € ${productVariant.buy_price}</span>` : ''}
                      </div>
                      `;
            },
          },
          {
            text: 'Stock',
            value: 'stock',
            align: 'right',
            class: 'text-right',
            transform: (stock: string, productVariant: ProductVariant) => {
              return `<div class="text-right">
                      <span class="d-block white-space-no-wrap">AV:  ${stock}</span>
                      </div>
                      `;
            },
          },
          {
            text: 'QTY',
            value: 'qty',
            align: 'right',
            class: 'text-right',
            slot: 'qty',
            width: '100px'
          },
          {
            text: '',
            value: 'actions',
            align: 'right',
            class: 'text-right',
            slot: 'add_action',
          },
        ],
        actions: [],
        filters: [],
      }
  }

  // #endregion

  // #region @Watchers
  // #endregion
}

export interface FastProductVariantWithQuantity extends FastProductVariant {
  quantity?: number;
}
